import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";

export interface GenerateAllParams {
  pipeline: "v1" | "v2";
  examples: "none" | "retrieved";
  model: "gemini" | "qwen" | "gemma" | "deepseek-pro" | "deepseek-flash" | "gemini-flash";
}

interface GenerateAllDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerateAll: (params: GenerateAllParams) => void;
  variantCount: number;
  regenerate?: boolean;
}

export function GenerateAllDialog({
  open,
  onOpenChange,
  onGenerateAll,
  variantCount,
  regenerate = false,
}: GenerateAllDialogProps) {
  const [selection, setSelection] = useState<"v1" | "v2-b" | "v2-c">("v2-c");
  const [model, setModel] = useState<GenerateAllParams["model"]>("gemini-flash");
  const changeOpen = (next: boolean) => {
    if (!next) {
      setSelection("v2-c");
      setModel("gemini-flash");
    }
    onOpenChange(next);
  };
  const action = regenerate ? "Regenerer" : "Generer";
  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{action} alle vurderinger</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-admin-text-secondary">
          {action} {variantCount} varianter. Legegodkjente vurderinger beholdes.
        </p>
        <div className="space-y-1">
          <label
            htmlFor="assessment-pipeline"
            className="text-xs font-medium text-admin-text-primary"
          >
            AI-pipeline
          </label>
          <Select
            id="assessment-pipeline"
            value={selection}
            onChange={(e) =>
              setSelection(
                e.target.value === "v2-c" ? "v2-c" : e.target.value === "v2-b" ? "v2-b" : "v1",
              )
            }
          >
            <option value="v1">v1</option>
            <option value="v2-b">
              v2 B — fakta og legeveiledning, uten eksempler
            </option>
            <option value="v2-c">
              v2 C — med lignende legegodkjente eksempler (standard)
            </option>
          </Select>
        </div>
        <div className="space-y-1">
          <label
            htmlFor="assessment-model"
            className="text-xs font-medium text-admin-text-primary"
          >
            Modell
          </label>
          <Select
            id="assessment-model"
            value={model}
            onChange={(e) => setModel(e.target.value as GenerateAllParams["model"])}
          >
            <option value="gemini">Gemini 2.5 Pro</option>
            <option value="qwen">Qwen — selvhostet, kun til test</option>
            <option value="gemma">Gemma — selvhostet, kun til test</option>
            <option value="deepseek-pro">DeepSeek V4 Pro — EU via Requesty, kun til test</option>
            <option value="deepseek-flash">DeepSeek V4.1 Flash — EU via Requesty, kun til test</option>
            <option value="gemini-flash">Gemini 3.8 Flash — EU via Requesty (standard)</option>
          </Select>
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={() => changeOpen(false)}>
            Avbryt
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onGenerateAll({
                pipeline: selection === "v1" ? "v1" : "v2",
                examples: selection === "v2-c" ? "retrieved" : "none",
                model,
              });
              changeOpen(false);
            }}
          >
            {action} alle ({variantCount})
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
